<template>
  <article>
    <header v-if="selectedAgent">
      <h1 class="is-size-4 has-text-weight-semibold">
        {{ selectedAgent.trivial_name | capitalizeFirstLetter }}
        <div
          class="tag has-text-white is-padded-small"
          :class="backgroundByAgentCategory(selectedAgent.agent_type)"
        >
          {{ agentCategoryLabel(selectedAgent.type_field) }}
        </div>
      </h1>
      <a
        target="_blank"
        rel="noreferrer"
        :href="`/agents/${selectedAgent.slug}/summary`"
      >Summary</a
      >
      <p>{{ selectedAgent.name }}</p>
      <p>Cas: {{ selectedAgent.cas_number }}</p>
      <p>
        UN:
        <span
          v-for="(un, idx) in selectedAgent.un_numbers"
          :key="idx">
          {{ un.un_number }}
        </span>
      </p>
      <p>
        EC:
        <span
          v-for="(ec, idx) in selectedAgent.ec_numbers"
          :key="idx">
          {{ ec.ec_number }}
        </span>
      </p>
      <p>
        <pdf-link
          v-if="selectedAgent.info_pdf"
          :url="selectedAgent.info_pdf" />
      </p>
    </header>
    <b-columns v-if="loadingAgents">
      <b-column class="is-full has-text-centered">
        <loading-spinner />
      </b-column>
    </b-columns>
    <b-columns v-else-if="isBioMarker">
      <b-column class="is-full">
        <collapsible-card
          heading="Features"
          open>
          <agent-features />
        </collapsible-card>
      </b-column>
    </b-columns>
    <b-columns
      v-else
      is-multiline>
      <b-column
        class="is-full"
        v-if="isCAgent">
        <collapsible-card
          heading="GHS Statements & Pictograms"
          :open="this.getLayout[0]"
        >
          <section>
            <h1 class="is-size-5">GHS Statements & Pictograms</h1>
            <agent-ghs-statements :statements="agentStatements" />
            <hr >
            <agent-ghs-pictograms :pictograms="agentPictograms" />
            <div class="warning-labels">
              <div class="summary-firediamond">
                <fire-diamond
                  v-if="agentFireDiamond"
                  class="has-text-centered-mobile"
                  :input="agentFireDiamond"
                  :size="125"
                />
              </div>
              <div class="orange-shield">
                <div>
                  {{ selectedAgent.hazard_id }}
                </div>
                <div>
                  <span
                    v-for="(un, idx) in selectedAgent.un_numbers"
                    :key="idx"
                  >
                    {{ un.un_number }}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </collapsible-card>
      </b-column>
      <b-column class="is-full">
        <collapsible-card
          heading="Observations"
          :open="this.getLayout[0]" >
          <div>Color:</div>
          <div>{{ selectedAgent && selectedAgent.colors && selectedAgent.colors.join(', ') }}</div>
          <br>
          <div>Taste:</div>
          <div>{{ selectedAgent && selectedAgent.tastes && selectedAgent.tastes.join(', ') }}</div>
          <br>
          <div>Odor:</div>
          <div>{{ selectedAgent && selectedAgent.odors && selectedAgent.odors.join(', ') }}</div>
          <br>
          <div>State:</div>
          <div>{{ selectedAgent && selectedAgent.states && selectedAgent.states.join(', ') }}</div>
          <br>
          <div>Possible sources:</div>
          <div>{{ selectedAgent && selectedAgent.possible_sources && selectedAgent.possible_sources.join(', ') }}</div>
        </collapsible-card>
      </b-column>
      <b-column class="is-full">
        <collapsible-card
          heading="Evacuation"
          :open="this.getLayout[2]">
          <agent-evacuation />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Protection"
          :open="this.getLayout[0]">
          <agent-protection />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="!isRNAgent, isFirstResponder"
      >
        <collapsible-card
          heading="Rescue"
          :open="this.getLayout[1]">
          <agent-rescue />
        </collapsible-card>
      </b-column>
      <b-column class="is-full">
        <collapsible-card
          heading="Treatment"
          :open="this.getLayout[3]">
          <agent-treatment />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Health hazard values"
          :open="this.getLayout[4]"
        >
          <agent-health-hazard-values />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Physical properties"
          :open="this.getLayout[4]"
        >
          <agent-physical-properties />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Chemical attributes"
          :open="this.getLayout[4]"
        >
          <agent-chemical-attributes-part />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Decontamination"
          :open="this.getLayout[6]">
          <agent-decontamination />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Detectors"
          :open="this.getLayout[6]">
          <agent-detectors />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card
          heading="Sampling"
          :open="this.getLayout[6]">
          <agent-sampling />
        </collapsible-card>
      </b-column>
      <b-column class="is-full">
        <collapsible-card heading="Links">
          <agent-links />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card heading="Spectra">
          <agent-spectra />
        </collapsible-card>
      </b-column>
      <b-column
        class="is-full"
        v-if="isFirstResponder">
        <collapsible-card heading="Other information" >
          <div>
            <p class="bold">Level 1: {{ warnings && warnings[0] && warnings[0].title || "" }}</p>
            <p>{{ warnings && warnings[0] && warnings[0].warning || "" }}</p>
          </div>
          <br>
          <div>
            <p class="bold">Level 2: {{ warnings && warnings[1] && warnings[1].title || "" }}</p>
            <p>{{ warnings && warnings[1] && warnings[1].warning || "" }}</p>
          </div>
          <br>
          <div>
            <p class="bold">Level 3: {{ warnings && warnings[2] && warnings[2].title || "" }}</p>
            <p>{{ warnings && warnings[2] && warnings[2].warning || "" }}</p>
          </div>
          <br>
        </collapsible-card>
      </b-column>
    </b-columns>
  </article>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { rolesEnum, agentTypeEnum } from '@/store'
import CollapsibleCard from '@/components/structures/CollapsibleCard'
import AgentProtection from '@/components/agent/AgentProtection'
import AgentRescue from '@/components/agent/rescue/AgentRescue'
import AgentDecontamination from '@/components/agent/decontamination/AgentDecontamination'
import AgentEvacuation from '@/components/agent/AgentEvacuation'
import AgentTreatment from '@/components/agent/treatment/AgentTreatment'
import AgentDetectors from '@/components/agent/detectors/AgentDetectors'
import AgentSampling from '@/components/agent/sampling/AgentSampling'
import AgentLinks from '@/components/agent/links/AgentLinks'
import AgentPhysicalProperties from '@/components/agent/features/attributes/chemical/AgentPhysicalProperties'
import AgentHealthHazardValues from '../components/agent/features/attributes/chemical/AgentHealthHazardValues'
import AgentChemicalAttributesPart from '../components/agent/features/attributes/chemical/AgentChemicalAttributesPart'
import { colorByAgentCategory } from '@/mixins'
import AgentGhsStatements from '@/components/agent/features/attributes/chemical/AgentGhsStatements'
import AgentGhsPictograms from '@/components/agent/features/attributes/chemical/AgentGhsPictograms'
import FireDiamond from '@/components/agent/rescue/FireDiamond'

import AgentSpectra from '@/components/agent/spectra/AgentSpectra'

export default {
  components: {
    CollapsibleCard,
    AgentEvacuation,
    AgentProtection,
    AgentRescue,
    AgentDecontamination,
    AgentTreatment,
    AgentDetectors,
    AgentSampling,
    AgentLinks,
    AgentPhysicalProperties,
    AgentHealthHazardValues,
    AgentChemicalAttributesPart,
    AgentGhsStatements,
    AgentGhsPictograms,
    FireDiamond,
    AgentSpectra,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      roles: rolesEnum,
    }
  },
  mounted () {
    this.fetchAgent(this.slug)
  },
  computed: {
    ...mapGetters([
      'selectedRole',
      'loadingAgents',
      'selectedAgent',
      'agentCategoryLabel',
      'agentStatements',
      'agentPictograms',
      'agentFireDiamond',
    ]),
    isRNAgent () {
      return this.selectedAgent
        ? this.selectedAgent.agent_type === agentTypeEnum.RNAgent
        : false
    },
    isBAgent () {
      return this.selectedAgent
        ? this.selectedAgent.agent_type === agentTypeEnum.BAgent
        : false
    },
    isCAgent () {
      return this.selectedAgent
        ? this.selectedAgent.agent_type === agentTypeEnum.CAgent
        : false
    },
    isBioMarker () {
      return this.selectedAgent
        ? this.selectedAgent.agent_type === agentTypeEnum.BioMarker
        : false
    },
    getLayout () {
      const open = true
      const closed = false
      switch (this.selectedRole) {
        case rolesEnum.FIRST_RESPONDER:
          return [open, open, open, open, closed, closed, closed]
        case rolesEnum.DECON_OR_SAMPLER:
          return [open, closed, closed, closed, open, open, open]
      }
    },
    isFirstResponder () {
      return this.selectedRole === rolesEnum.FIRST_RESPONDER
    },
    warnings () {
      return this.selectedAgent && this.selectedAgent.warnings
    }
  },
  mixins: [colorByAgentCategory],
  methods: {
    ...mapActions(['fetchAgent']),
  },
}
</script>

<style lang="scss">
.tag {
  vertical-align: middle;
}
.bold {
  font-weight: bold;
}
</style>
